import React from 'react';
import { Container, Alert, Spinner } from 'reactstrap';
import { Input as CustomInput } from '@components/Input';
import { Button as CustomButton, buttonVariants } from '@components/Button';
import { EmptyState } from '@components/EmptyState';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Table } from '@/components/Table';
import { Pagination } from '@/components/Pagination';
import { Text, fontWeightEnum, textVariantsEnum } from '@/components/Text';
import styles from './EmployeeList.module.scss';
import { Dropdown, borderedSelectStyles } from '@/components/Dropdown';
import { ROLE_OPTIONS } from '@/constants/roles';
import { AddTeamModal } from '@/views/Teams/components/TeamsTab/AddTeamModal.component';
import { PillDropdown } from '@components/PillDropdown';
import { StatCard } from '@/components/StatCard';
import { EmployeeAvatarHeader } from './components/EmployeeAvatarHeader.component';
import { employeeColumnsConfig } from './EmployeeList.config';
import { BsBuildings } from 'react-icons/bs';
import { MdAdminPanelSettings } from 'react-icons/md';
import {
  FiUsers,
  FiUserCheck,
  FiShield,
  FiMail,
  FiDownload,
  FiPlus,
  FiCreditCard,
  FiGlobe,
  FiMonitor,
  FiTarget,
  FiInfo,
  FiCheckCircle,
  FiSave,
} from 'react-icons/fi';
import { CopyIconButton } from '@components/CopyIconButton';
import { FormSectionCard } from '@components/FormSectionCard';
import { InfoBox } from '@components/InfoBox';
import { ConfirmActionDialog } from '@components/ConfirmActionDialog';
import { ActionsMenu } from '@components/ActionsMenu';

// Distinct from ROLE_OPTIONS above (used by the Edit dialog) — this powers
// the page-level Role filter pill, which needs an "All Roles" option.

// Pure presentational — no Redux/react-query/service calls. Rendered by both
// EmployeeList.component.jsx (real, Firestore/Redux-backed) and
// EmployeeList.demo.component.jsx (static fixture-backed); a future redesign
// only ever needs to touch this file plus whichever container's props it
// changes.
export const EmployeeListView = ({
  error,
  userProfile,
  selectedCompany,
  selectedTeam,
  teamOptions,
  loadingTeams,
  onTeamChange,
  roleFilter,
  roleFilterOptions,
  onRoleFilterChange,
  showAddTeamButton,
  onInviteEmployeeClick,
  onAddDepartmentClick,
  onExportCSV,
  roleCounts,
  activeTab,
  onTabChange,
  toastRef,
  deleteConfirmVisible,
  onHideDeleteConfirm,
  deleteConfirmMessage,
  onConfirmDeleteUserFromTeam,
  removeFromCompanyConfirmVisible,
  onHideRemoveFromCompanyConfirm,
  removeFromCompanyMessage,
  onConfirmRemoveFromCompany,
  removeFromCompanyPending,
  actionsMenuItems,
  menuRef,
  moreDetailsUserId,
  onCloseMoreDetailsDialog,
  moreDetailsRosterRow,
  loadingExtendedDetails,
  extendedDetailsError,
  extendedDetailsQueryError,
  extendedDetails,
  employeeDetailFields,
  onFieldCopied,
  onFieldCopyError,
  isExistingTeamAdmin,
  addToAnotherTeamDialogVisible,
  onCloseAddToAnotherTeamDialog,
  submittingAddToAnotherTeam,
  onSubmitAddToAnotherTeam,
  loadingAddToAnotherTeamActiveSection,
  hasActiveTrackingChanges,
  loadingAddToAnotherTeamCandidates,
  addToAnotherTeamCompany,
  addToAnotherTeamRole,
  addToAnotherTeamTeams,
  companyOptions,
  onAddToAnotherTeamCompanyChange,
  onAddToAnotherTeamTeamsChange,
  onAddToAnotherTeamRoleChange,
  addToAnotherTeamCandidateTeams,
  addToAnotherTeamCompanyTeamCount,
  addToAnotherTeamUser,
  addToAnotherTeamActiveCompanyOptions,
  addToAnotherTeamActiveCompany,
  onAddToAnotherTeamActiveCompanyChange,
  addToAnotherTeamActiveTeamOptions,
  addToAnotherTeamActiveTeam,
  onAddToAnotherTeamActiveTeamChange,
  changeActiveTeamDialogVisible,
  onCloseChangeActiveTeamDialog,
  submittingChangeActiveTeam,
  onSubmitChangeActiveTeam,
  loadingChangeActiveTeamModal,
  changeActiveTeamCompany,
  changeActiveTeamTeam,
  hasChangeActiveTeamPendingSave,
  changeActiveTeamUser,
  changeActiveTeamCompanyOptions,
  onChangeActiveTeamCompanySelect,
  changeActiveTeamTeamOptions,
  onChangeActiveTeamTeamChange,
  editDialogVisible,
  onHideEditDialog,
  editRow,
  editRole,
  onCancelEditDialog,
  onSaveEditDetails,
  updateEmployeeDetailsPending,
  hasEditFormChanges,
  editDeskId,
  onEditDeskIdChange,
  onEditRoleChange,
  editTimeZone,
  onEditTimeZoneChange,
  timezoneOptions,
  pickActiveTeamDialogVisible,
  onClosePickActiveTeamDialog,
  removeUserFromTeamPending,
  onConfirmDeleteAfterPickingActiveTeam,
  selectedReplacementTeam,
  replacementTeamOptions,
  onSelectedReplacementTeamChange,
  companiesError,
  teamsError,
  rosterError,
  rosterQueryError,
  employees,
  loadingEmployees,
  searchTerm,
  onSearchTermChange,
  sortedEmployees,
  pagedEmployees,
  onSortClick,
  sortedBy,
  reverseSort,
  currentPage,
  totalPages,
  onPageChange,
  pageSize,
  onActionsButtonClick,
  userUID,
  IntegrationImportTabComponent,
  showAddTeamModal,
  onCloseAddTeamModal,
  onTeamCreated,
  addTeamDefaultCompanyId,
}) => {
  const employeeColumns = employeeColumnsConfig({
    selectedTeamLabel: selectedTeam?.label,
    onActionsButtonClick,
  });

  if (error && userProfile?.activeCompany?.value !== 'Admin') {
    return (
      <Container className="mt-4">
        <Alert color="danger">{error}</Alert>
      </Container>
    );
  }
  return (
    <>
      <div className={styles.employeeListPage}>
        <div className={styles.employeeListContainer}>
          <div className={styles.employeeListHeader}>
            <div className={styles.employeeListDropdownContainer}>
              {/* Company dropdown commented out — Team is now driven by the
                  header's active company (selectedCompany still defaults to
                  activeCompanyId and stays wired for CSV export, deep-linking,
                  Add to Another Team, etc.). */}
              {/* <PillDropdown
                icon={<BsBuildings />}
                label="Company"
                value={selectedCompany?.value ?? null}
                options={companyOptions}
                loading={loadingCompanies}
                placeholder="Select Company"
                onChange={(val) => {
                  setSelectedCompany(
                    companyOptions.find((o) => o.value === val) || null
                  );
                  setSelectedTeam(null);
                  setError(null);
                }}
              /> */}
              <PillDropdown
                icon={<FiUsers />}
                label="Department"
                value={selectedTeam?.value ?? null}
                options={teamOptions}
                loading={loadingTeams}
                disabled={!selectedCompany || loadingTeams}
                placeholder="Select Department"
                onChange={onTeamChange}
              />
              <PillDropdown
                icon={<FiUserCheck />}
                label="Role"
                value={roleFilter}
                options={roleFilterOptions}
                onChange={onRoleFilterChange}
              />
            </div>
            <div className={styles.employeeListHeaderActions}>
              {showAddTeamButton ? (
                <>
                  <CustomButton
                    text="Invite Employee"
                    icon={<FiPlus />}
                    onClick={onInviteEmployeeClick}
                    variant={buttonVariants.primary_rounded}
                  />
                  <CustomButton
                    text="Add Department"
                    icon={<FiPlus />}
                    onClick={onAddDepartmentClick}
                    variant={buttonVariants.secondary}
                    className={styles.headerActionButton}
                  />
                </>
              ) : null}
              <CustomButton
                text="Export to CSV"
                icon={<FiDownload />}
                onClick={onExportCSV}
                variant={buttonVariants.secondary}
                className={styles.headerActionButton}
              />
            </div>
          </div>
          <div className={styles.statsRow}>
            <StatCard
              icon={<FiUsers />}
              iconBg="var(--blue-100)"
              label="Total Employees"
              value={roleCounts.total}
            />
            <StatCard
              icon={<FiUserCheck />}
              iconBg="var(--purple-100)"
              label="Managers"
              value={roleCounts.managers}
            />
            <StatCard
              icon={<FiShield />}
              iconBg="var(--success-100)"
              label="Admins"
              value={roleCounts.admins}
            />
            {/* <StatCard
              icon={<FiMail />}
              iconBg="var(--warning-100)"
              label="Pending Invites"
              value="—"
            /> */}
          </div>
          <div className={styles.tabBar}>
            <button
              className={`${styles.tab} ${activeTab === 'employees' ? styles.tabActive : ''}`}
              onClick={() => onTabChange('employees')}
            >
              Employees
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'quickbooks' ? styles.tabActive : ''}`}
              onClick={() => onTabChange('quickbooks')}
            >
              QuickBooks
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'salesforce' ? styles.tabActive : ''}`}
              onClick={() => onTabChange('salesforce')}
            >
              Salesforce
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'sagehr' ? styles.tabActive : ''}`}
              onClick={() => onTabChange('sagehr')}
            >
              Sage HR
            </button>
            <button
              className={`${styles.tab} ${activeTab === 'zohopeople' ? styles.tabActive : ''}`}
              onClick={() => onTabChange('zohopeople')}
            >
              Zoho People
            </button>
          </div>
          <div className={styles.employeeListTableContainer}>
            <Toast ref={toastRef} />
            <ConfirmActionDialog
              visible={deleteConfirmVisible}
              onHide={onHideDeleteConfirm}
              title="Delete user"
              message={deleteConfirmMessage}
              confirmLabel="Delete user"
              onConfirm={onConfirmDeleteUserFromTeam}
            />
            <ConfirmActionDialog
              visible={removeFromCompanyConfirmVisible}
              onHide={onHideRemoveFromCompanyConfirm}
              title="Remove from company"
              message={removeFromCompanyMessage}
              confirmLabel="Remove User"
              onConfirm={onConfirmRemoveFromCompany}
              loading={removeFromCompanyPending}
            />
            <ActionsMenu items={actionsMenuItems} ref={menuRef} />
            <Dialog
              visible={Boolean(moreDetailsUserId)}
              style={{ width: '600px' }}
              onHide={onCloseMoreDetailsDialog}
              header={
                moreDetailsRosterRow ? (
                  <EmployeeAvatarHeader
                    name={moreDetailsRosterRow.name}
                    role={moreDetailsRosterRow.role}
                  />
                ) : null
              }
              footer={
                <CustomButton
                  text="Close"
                  variant={buttonVariants.secondary}
                  onClick={onCloseMoreDetailsDialog}
                />
              }
            >
              {moreDetailsRosterRow ? (
                <div className={styles.employeeDetailsCard}>
                  {loadingExtendedDetails && moreDetailsUserId ? (
                    <div className="text-center py-4">
                      <Spinner color="primary" />
                      <p className="mt-2 mb-0 small text-muted">
                        Loading additional details…
                      </p>
                    </div>
                  ) : extendedDetailsError && moreDetailsUserId ? (
                    <Alert color="danger" className="mt-2 mb-0">
                      {extendedDetailsQueryError?.message ||
                        'Failed to load additional details'}
                    </Alert>
                  ) : extendedDetails ? (
                    <div className={styles.employeeDetailsGrid}>
                      {employeeDetailFields.map((field) => (
                        <StatCard
                          key={field.key}
                          size="compact"
                          icon={field.icon}
                          iconBg={field.iconBg}
                          label={field.label}
                          value={field.value}
                          mono={field.mono}
                          truncate={field.truncate}
                          fullWidth={field.fullWidth}
                          action={
                            field.copyLabel ? (
                              <CopyIconButton
                                value={field.value}
                                label={field.copyLabel}
                                onCopied={onFieldCopied}
                                onError={onFieldCopyError}
                              />
                            ) : null
                          }
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </Dialog>
            <Dialog
              header={
                <div className={styles.dialogHeaderBlock}>
                  <Text
                    variant={textVariantsEnum.Text_lg}
                    fontWeight={fontWeightEnum.Semibold}
                  >
                    {isExistingTeamAdmin
                      ? 'Manage tracking settings'
                      : 'Add to another company & department'}
                  </Text>
                  <Text
                    variant={textVariantsEnum.Text_sm}
                    fontWeight={fontWeightEnum.Normal}
                    color="gray-600"
                  >
                    {isExistingTeamAdmin
                      ? 'Control where this employee is tracking time and activity.'
                      : "Manage this employee's membership and active tracking settings."}
                  </Text>
                </div>
              }
              visible={addToAnotherTeamDialogVisible}
              style={{ width: '600px' }}
              onHide={onCloseAddToAnotherTeamDialog}
              footer={
                <div>
                  <CustomButton
                    text="Cancel"
                    variant={buttonVariants.secondary}
                    onClick={onCloseAddToAnotherTeamDialog}
                    disabled={submittingAddToAnotherTeam}
                  />
                  <CustomButton
                    text="Save Changes"
                    icon={<FiSave />}
                    variant={buttonVariants.primary}
                    onClick={onSubmitAddToAnotherTeam}
                    loading={submittingAddToAnotherTeam}
                    disabled={
                      submittingAddToAnotherTeam ||
                      loadingAddToAnotherTeamActiveSection ||
                      (isExistingTeamAdmin
                        ? !addToAnotherTeamActiveCompany?.value ||
                          !addToAnotherTeamActiveTeam?.value ||
                          !hasActiveTrackingChanges
                        : loadingAddToAnotherTeamCandidates ||
                          !addToAnotherTeamCompany?.value ||
                          !addToAnotherTeamRole?.value ||
                          (addToAnotherTeamRole?.value !== 'Admin' &&
                            addToAnotherTeamTeams.length === 0))
                    }
                  />
                </div>
              }
            >
              <div className={styles.stackedSectionsContainer}>
                {isExistingTeamAdmin && (
                  <InfoBox
                    variant="info"
                    className={styles.adminNoticeBanner}
                    icon={<MdAdminPanelSettings size={20} />}
                    iconBg="#5b4dff"
                  >
                    <Text
                      variant={textVariantsEnum.Text_sm}
                      fontWeight={fontWeightEnum.Semibold}
                      color="gray-900"
                    >
                      This user is an Admin
                    </Text>
                    <Text
                      variant={textVariantsEnum.Text_sm}
                      fontWeight={fontWeightEnum.Normal}
                      color="gray-600"
                    >
                      Admins have access to all departments by default.
                    </Text>
                  </InfoBox>
                )}
                {!isExistingTeamAdmin && (
                  <FormSectionCard
                    stepNumber={1}
                    icon={<FiUsers size={20} color="var(--blue-600)" />}
                    iconBg="var(--blue-100)"
                    title="Add to another company & department"
                    description="Add this employee to another company and department with specific access."
                  >
                    <div className={styles.editFormGrid}>
                      <Dropdown
                        label="Company *"
                        value={addToAnotherTeamCompany}
                        selectStyles={borderedSelectStyles}
                        options={companyOptions}
                        onChange={onAddToAnotherTeamCompanyChange}
                        placeholder="Select company"
                        isDisabled={submittingAddToAnotherTeam}
                      />
                      {addToAnotherTeamRole?.value !== 'Admin' && (
                        <Dropdown
                          label="Department *"
                          value={addToAnotherTeamTeams}
                          selectStyles={borderedSelectStyles}
                          options={addToAnotherTeamCandidateTeams}
                          onChange={onAddToAnotherTeamTeamsChange}
                          isMulti
                          placeholder={
                            loadingAddToAnotherTeamCandidates
                              ? 'Loading departments...'
                              : 'Select department'
                          }
                          isDisabled={
                            submittingAddToAnotherTeam ||
                            loadingAddToAnotherTeamCandidates
                          }
                        />
                      )}
                      <div className={styles.gridColumnFull}>
                        <Dropdown
                          label="Role *"
                          value={addToAnotherTeamRole}
                          selectStyles={borderedSelectStyles}
                          options={ROLE_OPTIONS}
                          onChange={onAddToAnotherTeamRoleChange}
                          placeholder="Select Role"
                          isDisabled={submittingAddToAnotherTeam}
                        />
                      </div>
                    </div>

                    {!loadingAddToAnotherTeamCandidates &&
                      addToAnotherTeamRole?.value !== 'Admin' &&
                      addToAnotherTeamCandidateTeams.length === 0 && (
                        <Text
                          variant={textVariantsEnum.Text_sm}
                          fontWeight={fontWeightEnum.Normal}
                          color="gray-600"
                        >
                          User is already in all departments for this company.
                        </Text>
                      )}

                    <InfoBox variant="info" icon={<FiInfo size={16} />}>
                      {addToAnotherTeamRole?.value === 'Admin' ? (
                        <>
                          Admin access is company-wide —{' '}
                          <strong>
                            {addToAnotherTeamUser?.name || 'this employee'}
                          </strong>{' '}
                          will be added to all{' '}
                          {addToAnotherTeamCompanyTeamCount} department(s) in
                          the selected company. It will not change their active
                          company or department.
                        </>
                      ) : (
                        <>
                          This will add{' '}
                          <strong>
                            {addToAnotherTeamUser?.name || 'this employee'}
                          </strong>{' '}
                          as a member of the selected company and department. It
                          will not change their active company or department.
                        </>
                      )}
                    </InfoBox>
                  </FormSectionCard>
                )}

                <FormSectionCard
                  stepNumber={isExistingTeamAdmin ? undefined : 2}
                  icon={<FiTarget size={20} color="var(--success-600)" />}
                  iconBg="var(--success-100)"
                  title="Active company & department (tracking)"
                  description="Set the company and department where this employee's time and activity will be tracked."
                >
                  {loadingAddToAnotherTeamActiveSection &&
                  !addToAnotherTeamActiveCompanyOptions.length ? (
                    <div className="text-center py-3">
                      <Spinner color="primary" size="sm" />
                      <p className="mt-2 mb-0 small text-muted">
                        Loading active company and department…
                      </p>
                    </div>
                  ) : addToAnotherTeamActiveCompanyOptions.length === 0 ? (
                    <Text
                      variant={textVariantsEnum.Text_sm}
                      fontWeight={fontWeightEnum.Normal}
                      color="gray-600"
                    >
                      No companies on file for this member; active company and
                      department cannot be edited here.
                    </Text>
                  ) : (
                    <>
                      <div className={styles.editFormGrid}>
                        <Dropdown
                          label="Active company *"
                          value={addToAnotherTeamActiveCompany}
                          selectStyles={borderedSelectStyles}
                          options={addToAnotherTeamActiveCompanyOptions}
                          onChange={onAddToAnotherTeamActiveCompanyChange}
                          placeholder="Select company"
                          isDisabled={
                            submittingAddToAnotherTeam ||
                            loadingAddToAnotherTeamActiveSection
                          }
                        />
                        <Dropdown
                          label="Active department *"
                          value={addToAnotherTeamActiveTeam}
                          selectStyles={borderedSelectStyles}
                          options={addToAnotherTeamActiveTeamOptions}
                          onChange={onAddToAnotherTeamActiveTeamChange}
                          placeholder={
                            loadingAddToAnotherTeamActiveSection
                              ? 'Loading departments…'
                              : 'Select department'
                          }
                          isDisabled={
                            submittingAddToAnotherTeam ||
                            loadingAddToAnotherTeamActiveSection ||
                            !addToAnotherTeamActiveTeamOptions.length
                          }
                        />
                      </div>
                      {!loadingAddToAnotherTeamActiveSection &&
                        addToAnotherTeamActiveCompany &&
                        addToAnotherTeamActiveTeamOptions.length === 0 && (
                          <Text
                            variant={textVariantsEnum.Text_sm}
                            fontWeight={fontWeightEnum.Normal}
                            color="gray-600"
                          >
                            This member has no departments in the selected
                            active company.
                          </Text>
                        )}
                      <InfoBox
                        variant="success"
                        icon={<FiCheckCircle size={16} />}
                      >
                        Time, activity, and productivity data will be recorded
                        under the selected{' '}
                        <strong>active company and department</strong>.
                      </InfoBox>
                    </>
                  )}
                </FormSectionCard>
              </div>
            </Dialog>
            <Dialog
              header={
                <div className={styles.dialogHeaderBlock}>
                  <Text
                    variant={textVariantsEnum.Text_lg}
                    fontWeight={fontWeightEnum.Semibold}
                  >
                    Change active department
                  </Text>
                  <Text
                    variant={textVariantsEnum.Text_sm}
                    fontWeight={fontWeightEnum.Normal}
                    color="gray-600"
                  >
                    Select the company and department where this employee&apos;s
                    time logs and activity will be recorded.
                  </Text>
                </div>
              }
              visible={changeActiveTeamDialogVisible}
              style={{ width: '520px' }}
              onHide={onCloseChangeActiveTeamDialog}
              footer={
                <div>
                  <CustomButton
                    text="Cancel"
                    variant={buttonVariants.secondary}
                    onClick={onCloseChangeActiveTeamDialog}
                    disabled={submittingChangeActiveTeam}
                  />
                  <CustomButton
                    text="Save Changes"
                    icon={<FiSave />}
                    variant={buttonVariants.primary}
                    onClick={onSubmitChangeActiveTeam}
                    loading={submittingChangeActiveTeam}
                    disabled={
                      submittingChangeActiveTeam ||
                      loadingChangeActiveTeamModal ||
                      !changeActiveTeamCompany?.value ||
                      !changeActiveTeamTeam?.value ||
                      !hasChangeActiveTeamPendingSave
                    }
                  />
                </div>
              }
            >
              <div className={styles.stackedSectionsContainer}>
                <EmployeeAvatarHeader
                  name={changeActiveTeamUser?.name}
                  role={changeActiveTeamUser?.role}
                  meta={`${changeActiveTeamUser?.role || '—'} • ${
                    selectedTeam?.label || '—'
                  }`}
                />
                <div className={styles.summaryDivider} />

                <FormSectionCard
                  icon={<BsBuildings size={20} color="var(--purple-600)" />}
                  iconBg="var(--purple-100)"
                  title="Active Company"
                  description="Choose the company where data should be recorded."
                >
                  {loadingChangeActiveTeamModal &&
                  !changeActiveTeamCompanyOptions.length ? (
                    <div className="text-center py-3">
                      <Spinner color="primary" size="sm" />
                    </div>
                  ) : (
                    <Dropdown
                      value={changeActiveTeamCompany}
                      selectStyles={borderedSelectStyles}
                      options={changeActiveTeamCompanyOptions}
                      onChange={onChangeActiveTeamCompanySelect}
                      placeholder="Select company"
                      isDisabled={
                        submittingChangeActiveTeam ||
                        loadingChangeActiveTeamModal
                      }
                    />
                  )}
                </FormSectionCard>

                <FormSectionCard
                  icon={<FiUsers size={20} color="var(--blue-600)" />}
                  iconBg="var(--blue-100)"
                  title="Active Department"
                  description="Choose the department within the company."
                >
                  <Dropdown
                    value={changeActiveTeamTeam}
                    selectStyles={borderedSelectStyles}
                    options={changeActiveTeamTeamOptions}
                    onChange={onChangeActiveTeamTeamChange}
                    placeholder={
                      loadingChangeActiveTeamModal
                        ? 'Loading departments…'
                        : 'Select department'
                    }
                    isDisabled={
                      submittingChangeActiveTeam ||
                      loadingChangeActiveTeamModal ||
                      !changeActiveTeamTeamOptions.length
                    }
                  />
                  {!loadingChangeActiveTeamModal &&
                    changeActiveTeamCompany &&
                    changeActiveTeamTeamOptions.length === 0 && (
                      <Text
                        variant={textVariantsEnum.Text_sm}
                        fontWeight={fontWeightEnum.Normal}
                        color={'gray-600'}
                      >
                        This member has no departments in the selected company.
                      </Text>
                    )}
                </FormSectionCard>

                <InfoBox variant="info" icon={<FiInfo size={16} />}>
                  All time logs, activity, and productivity data for this
                  employee will be tracked under the selected{' '}
                  <strong>company and department</strong>.
                </InfoBox>
              </div>
            </Dialog>
            <Dialog
              visible={editDialogVisible}
              style={{ width: '600px' }}
              onHide={onHideEditDialog}
              header={
                editRow ? (
                  <EmployeeAvatarHeader
                    name={editRow.name}
                    role={editRole || editRow.role}
                  />
                ) : null
              }
              footer={
                <div>
                  <CustomButton
                    text="Cancel"
                    variant={buttonVariants.secondary}
                    onClick={onCancelEditDialog}
                  />
                  <CustomButton
                    text="Save Changes"
                    icon={<FiSave />}
                    variant={buttonVariants.primary}
                    onClick={onSaveEditDetails}
                    loading={updateEmployeeDetailsPending}
                    disabled={
                      updateEmployeeDetailsPending ||
                      !hasEditFormChanges ||
                      !extendedDetails ||
                      loadingExtendedDetails
                    }
                  />
                </div>
              }
            >
              {editRow && (
                <div className={styles.employeeDetailsCard}>
                  {loadingExtendedDetails ? (
                    <div className="text-center py-4">
                      <Spinner color="primary" />
                      <p className="mt-2 mb-0">Loading member details…</p>
                    </div>
                  ) : extendedDetailsError ? (
                    <Alert color="danger">
                      {extendedDetailsQueryError?.message ||
                        'Failed to load member details'}
                    </Alert>
                  ) : extendedDetails ? (
                    <div className={styles.editFormGrid}>
                      <CustomInput
                        label="Email Address"
                        type="text"
                        value={editRow.email}
                        readOnly
                        disabled
                        leftBlock={<FiMail size={16} color="var(--gray-500)" />}
                      />

                      <CustomInput
                        label="Employee ID"
                        type="text"
                        value={extendedDetails.employeeId}
                        readOnly
                        disabled
                        leftBlock={
                          <FiCreditCard size={16} color="var(--gray-500)" />
                        }
                      />

                      <CustomInput
                        label="Desk ID (Optional)"
                        type="text"
                        value={editDeskId}
                        onChange={onEditDeskIdChange}
                        placeholder="Enter desk Id"
                        leftBlock={
                          <FiMonitor size={16} color="var(--gray-500)" />
                        }
                      />

                      <Dropdown
                        selectStyles={borderedSelectStyles}
                        label="Role"
                        leftIcon={
                          <FiShield size={16} color="var(--gray-500)" />
                        }
                        value={{ label: editRole, value: editRole }}
                        options={ROLE_OPTIONS}
                        onChange={onEditRoleChange}
                        placeholder="Select Role"
                        className="w-100"
                      />

                      <Dropdown
                        selectStyles={borderedSelectStyles}
                        label="Time Zone"
                        leftIcon={<FiGlobe size={16} color="var(--gray-500)" />}
                        value={{ label: editTimeZone, value: editTimeZone }}
                        options={timezoneOptions}
                        onChange={onEditTimeZoneChange}
                        placeholder="Select Time Zone"
                        filter
                        className="w-100"
                      />
                    </div>
                  ) : null}
                </div>
              )}
            </Dialog>
            <Dialog
              header={
                <Text
                  variant={textVariantsEnum.Text_lg}
                  fontWeight={fontWeightEnum.Semibold}
                >
                  Select new active department
                </Text>
              }
              visible={pickActiveTeamDialogVisible}
              style={{ width: '460px' }}
              onHide={onClosePickActiveTeamDialog}
              footer={
                <div>
                  <CustomButton
                    text="Cancel"
                    variant={buttonVariants.secondary}
                    onClick={onClosePickActiveTeamDialog}
                    disabled={removeUserFromTeamPending}
                  />
                  <CustomButton
                    text="Continue"
                    variant={buttonVariants.primary}
                    onClick={onConfirmDeleteAfterPickingActiveTeam}
                    loading={removeUserFromTeamPending}
                    disabled={
                      removeUserFromTeamPending ||
                      !selectedReplacementTeam?.value
                    }
                  />
                </div>
              }
            >
              <div className={styles.editFormContainer}>
                <Text
                  variant={textVariantsEnum.Text_sm}
                  fontWeight={fontWeightEnum.Normal}
                  color="gray-700"
                >
                  This user is currently being removed from their active
                  department. Choose which department should become active
                  before deleting.
                </Text>
                <Dropdown
                  label="New active department *"
                  value={selectedReplacementTeam}
                  selectStyles={borderedSelectStyles}
                  options={replacementTeamOptions}
                  onChange={onSelectedReplacementTeamChange}
                  placeholder="Select Department"
                  isDisabled={removeUserFromTeamPending}
                  leftIcon={<FiUsers size={14} color="var(--gray-500)" />}
                />
                <InfoBox variant="info" icon={<FiInfo size={16} />}>
                  All time logs, activity, and productivity data will be
                  recorded under the selected department.
                </InfoBox>
              </div>
            </Dialog>
            {(error || companiesError || teamsError || rosterError) && (
              <Alert color="danger" className="mb-4">
                {error ||
                  companiesError ||
                  teamsError ||
                  (rosterQueryError?.message
                    ? String(rosterQueryError.message)
                    : 'Failed to load department roster.')}
              </Alert>
            )}

            {activeTab === 'employees' ? (
              <>
                {/* Search - only when we have employees */}
                {selectedTeam && employees.length > 0 && !loadingEmployees && (
                  <div className={styles.tableToolbar}>
                    <div className={styles.toolbarSearch}>
                      <CustomInput
                        id="employee-search"
                        type="text"
                        placeholder="Search by name, email, or role..."
                        value={searchTerm}
                        onChange={onSearchTermChange}
                      />
                    </div>
                    {/* Not yet functional — hidden until wired up.
                    <div className={styles.toolbarActions}>
                      <CustomButton
                        text="Filters"
                        icon={<FiFilter />}
                        variant={buttonVariants.secondary}
                        className={styles.toolbarButton}
                      />
                      <CustomButton
                        text="Sort"
                        icon={<FiSliders />}
                        variant={buttonVariants.secondary}
                        className={styles.toolbarButton}
                      />
                      <CustomButton
                        text="Export"
                        icon={<FiDownload />}
                        variant={buttonVariants.secondary}
                        className={styles.toolbarButton}
                      />
                    </div>
                    */}
                  </div>
                )}

                {/* Employee Table */}
                {loadingEmployees ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-2">Loading employees...</p>
                  </div>
                ) : selectedTeam && employees.length > 0 ? (
                  sortedEmployees.length === 0 ? (
                    <EmptyState
                      illustration
                      title="No Employees Found"
                      description={
                        searchTerm.trim()
                          ? 'No employees match your search. Try adjusting your search term.'
                          : 'No employees have been added to this department yet.'
                      }
                    />
                  ) : (
                    <>
                      <Table
                        className={styles.employeeTable}
                        data={pagedEmployees}
                        columns={employeeColumns}
                        onSortClick={onSortClick}
                        sortedBy={sortedBy}
                        reverseSort={reverseSort}
                      />
                      <div className={styles.tableFooter}>
                        <Text
                          variant={textVariantsEnum.Text_sm}
                          color="gray-500"
                        >
                          Showing {(currentPage - 1) * pageSize + 1} to{' '}
                          {Math.min(
                            currentPage * pageSize,
                            sortedEmployees.length
                          )}{' '}
                          of {sortedEmployees.length} employees
                        </Text>
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={onPageChange}
                        />
                      </div>
                    </>
                  )
                ) : selectedTeam && employees.length === 0 ? (
                  <EmptyState
                    illustration
                    title="No Employees Found"
                    description="No employees found for the selected department. Try selecting a different department."
                  />
                ) : (
                  <Alert
                    style={{
                      backgroundColor: '#1F74EC',
                    }}
                  >
                    Please select a company and department to view employees.
                  </Alert>
                )}
              </>
            ) : (
              <IntegrationImportTabComponent
                companyId={selectedCompany?.value}
                teamId={selectedTeam?.value}
                userId={userUID}
                source={activeTab}
              />
            )}
          </div>
        </div>
      </div>
      <AddTeamModal
        isVisible={showAddTeamModal}
        onClose={onCloseAddTeamModal}
        callback={onTeamCreated}
        defaultCompanyId={addTeamDefaultCompanyId}
      />
    </>
  );
};
